import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Eye, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import type { DuplicateGroup} from '@repo/types';
import { jobsApi } from '@/lib/api/jobs.api';
import { format } from 'date-fns';

export const Route = createFileRoute('/admin/jobs/$id/duplicates')({
  component: JobDuplicatesPage,
});

function JobDuplicatesPage() {
  const { id } = Route.useParams();
  const [duplicateGroups, setDuplicateGroups] = useState<DuplicateGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState<DuplicateGroup | null>(null);
  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    loadDuplicates();
  }, [id]);

  const loadDuplicates = async () => {
    setIsLoading(true);
    try {
      const response = await jobsApi.getJobDuplicates(id);
      setDuplicateGroups(response.duplicateGroups);
    } catch (error) {
      console.log(error);
      toast.error('Failed to load duplicates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewComparison = (group: DuplicateGroup) => {
    setSelectedGroup(group);
    setShowComparison(true);
  };

  const handleKeepJob = async (groupId: string, jobIdToKeep: string) => {
    try {
      await jobsApi.resolveDuplicates(groupId, 'keep', jobIdToKeep, 'Kept single job');
      toast.success('Duplicate resolved - Job kept');
      loadDuplicates();
    } catch (error) {
      console.log(error);
      toast.error('Failed to resolve duplicate');
    }
  };

  const handleKeepBoth = async (groupId: string) => {
    try {
      await jobsApi.markNotDuplicate(groupId);
      toast.success('Both jobs kept - Marked as not duplicate');
      loadDuplicates();
    } catch (error) {
      console.log(error);
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="space-y-6">
      {/* Table */}
      <Card className="border-none shadow-none">
        <CardContent className="px-0">
          {isLoading ? (
            <div className="flex h-32 items-center justify-center text-muted-foreground">
              Loading duplicates...
            </div>
          ) : duplicateGroups.length === 0 ? (
            <div className="flex h-32 flex-col items-center justify-center gap-2">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
              <p className="font-medium">No duplicates found</p>
              <p className="text-sm text-muted-foreground">
                This job appears to be unique
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Duplicate Job</TableHead>
                    <TableHead className="w-[120px]">Similarity</TableHead>
                    <TableHead className="w-[200px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {duplicateGroups.map((group) => {
                    const duplicate = group.jobs.find((j) => j.id !== id) ?? group.jobs[1];
                    const similarity = Math.round(group.similarity * 100);
                    return (
                      <TableRow key={group.id}>
                        <TableCell>
                          <div className="space-y-1">
                            <Link to="/admin/jobs/$id" params={{ id: duplicate.id }}>
                              <Badge
                                variant="secondary"
                                className="mb-1 cursor-pointer font-mono hover:bg-secondary/80"
                              >
                                #{duplicate.jobKey}
                              </Badge>
                            </Link>
                            <p className="font-medium">{duplicate.title}</p>
                            <p className="text-sm text-muted-foreground">
                              {duplicate.company} • {duplicate.location}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {duplicate.viewCount} views • Posted by {duplicate.postedBy.name}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={similarity >= 90 ? 'destructive' : similarity >= 80 ? 'default' : 'secondary'}
                          >
                            {similarity}%
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewComparison(group)}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              Compare
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleKeepBoth(group.id)}
                            >
                              Keep Both
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Comparison Dialog */}
      <Dialog open={showComparison} onOpenChange={setShowComparison}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Job Comparison</DialogTitle>
            <DialogDescription>
              Review the differences between these jobs to determine if they are duplicates
            </DialogDescription>
          </DialogHeader>

          {selectedGroup && (
            <div className="space-y-6">
              {/* Side-by-side comparison */}
              <div className="grid gap-6 md:grid-cols-2">
                {selectedGroup.jobs.map((job, index) => (
                  <Card key={job.id} className={index === 0 ? 'border-primary' : ''}>
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        {/* Header */}
                        <div className="flex items-center justify-between">
                          <div>
                            <Link to="/admin/jobs/$id" params={{ id: job.id }}>
                              <Badge variant="secondary" className="mb-2 cursor-pointer font-mono">
                                #{job.jobKey}
                              </Badge>
                            </Link>
                            <h3 className="text-lg font-semibold">{job.title}</h3>
                          </div>
                          <Badge variant={job.status === 'active' ? 'default' : 'secondary'}>
                            {job.status}
                          </Badge>
                        </div>

                        <Separator />

                        {/* Company & Location */}
                        <div className="space-y-2">
                          <div>
                            <label className="text-xs font-medium text-muted-foreground">Company</label>
                            <p className="text-sm">{job.company}</p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-muted-foreground">Location</label>
                            <p className="text-sm">{job.location}</p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-muted-foreground">Industry</label>
                            <p className="text-sm">{job.industry}</p>
                          </div>
                        </div>

                        <Separator />

                        {/* Job Details */}
                        <div className="space-y-2">
                          <div>
                            <label className="text-xs font-medium text-muted-foreground">Job Type</label>
                            <p className="text-sm capitalize">{job.jobType}</p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-muted-foreground">Work Mode</label>
                            <p className="text-sm capitalize">{job.workMode}</p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-muted-foreground">Salary</label>
                            <p className="text-sm">
                              {job.salaryMin && job.salaryMax
                                ? `${job.salaryCurrency} ${job.salaryMin.toLocaleString()} - ${job.salaryMax.toLocaleString()}`
                                : 'Not specified'}
                            </p>
                          </div>
                        </div>

                        <Separator />

                        {/* Skills */}
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">Skills</label>
                          <div className="mt-2 flex flex-wrap gap-1">
                            {job.skills.map((skill) => (
                              <Badge key={skill} variant="outline" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        <Separator />

                        {/* Metadata */}
                        <div className="space-y-2 text-xs text-muted-foreground">
                          <p>
                            <span className="font-medium">Posted by:</span> {job.postedBy.name} (
                            {job.postedBy.role})
                          </p>
                          <p>
                            <span className="font-medium">Views:</span> {job.viewCount}
                          </p>
                          <p>
                            <span className="font-medium">Created:</span>{' '}
                            {format(new Date(job.createdAt), 'MMM d, yyyy HH:mm')}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Action Buttons */}
              <DialogFooter className="flex items-center justify-between sm:justify-between">
                <Button variant="outline" onClick={() => setShowComparison(false)}>
                  Cancel
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleKeepBoth(selectedGroup.id);
                      setShowComparison(false);
                    }}
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Keep Both Jobs
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => {
                      handleKeepJob(selectedGroup.id, selectedGroup.jobs[0].id);
                      setShowComparison(false);
                    }}
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Keep Source Job
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => {
                      handleKeepJob(selectedGroup.id, selectedGroup.jobs[1].id);
                      setShowComparison(false);
                    }}
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Keep Duplicate
                  </Button>
                </div>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
